import React, { useState } from "react";
import { Eye, EyeOff, Save } from "lucide-react";
import { api, formatError } from "../../lib/api";
import { ImageUpload } from "../ImageUpload";
import { SpecialtyPicker } from "../SpecialtyPicker";
import { toast } from "sonner";

const inputCls = "w-full h-12 rounded-xl border border-border px-4 outline-none focus:border-coral focus:ring-4 focus:ring-coral/10 font-medium bg-white text-base";
const BIO_MAX = 160;

export function ChefProfileForm({ chef, specialties, user, onSaved }) {
  const isNew = !chef;
  const [form, setForm] = useState({
    display_name: chef?.display_name || user?.name || "", bio: chef?.bio || "", city: chef?.city || "",
    specialties: chef?.specialties || [], avatar: chef?.avatar || user?.picture || "", status: chef?.status || "LIVE",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.display_name.trim()) { toast.error("Add your chef name"); return; }
    if (!form.avatar) { toast.error("A profile photo is required"); return; }
    if (form.bio.length > BIO_MAX) { toast.error(`Bio must be ${BIO_MAX} characters or fewer`); return; }
    setBusy(true);
    try {
      const { data } = isNew ? await api.post("/chef/apply", form) : await api.put("/kitchen/profile", form);
      toast.success(isNew ? "Welcome, Chef! Your profile is ready." : "Profile saved");
      onSaved?.(data, isNew);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-3xl bg-white border border-border shadow-soft p-6 sm:p-8" data-testid="chef-profile-form">
      {isNew && (
        <div className="mb-6">
          <h2 className="font-heading text-2xl sm:text-3xl font-black text-ink">Create your Chef Profile</h2>
          <p className="text-ink-soft mt-1">Tell the community who you are and what you love to cook. You can publish recipes right after.</p>
        </div>
      )}
      <div className="grid md:grid-cols-[auto_1fr] gap-8">
        <ImageUpload value={form.avatar} onChange={(v) => set("avatar", v)} label="Profile photo" shape="square" required testId="chef-photo-upload" hint="Square works best · min 480px" />
        <div className="space-y-5 min-w-0">
          <label className="block text-sm font-bold text-ink">Display name <span className="text-coral">*</span>
            <input className={`${inputCls} mt-1.5`} data-testid="chef-display-name" value={form.display_name} onChange={(e) => set("display_name", e.target.value)} placeholder="Chef name or kitchen brand" />
          </label>
          <label className="block text-sm font-bold text-ink">City
            <input className={`${inputCls} mt-1.5`} data-testid="chef-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Kolkata" />
          </label>
          <label className="block text-sm font-bold text-ink">
            <span className="flex items-center justify-between">Short bio <span className={`text-xs font-bold ${form.bio.length > BIO_MAX ? "text-coral" : "text-ink-soft"}`} data-testid="bio-count">{form.bio.length}/{BIO_MAX}</span></span>
            <textarea rows={2} maxLength={BIO_MAX + 20} className={`${inputCls} mt-1.5 h-auto py-3`} data-testid="chef-bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="What's your kitchen all about?" />
          </label>
          <div>
            <p className="text-sm font-bold text-ink mb-2">Culinary specialties</p>
            <SpecialtyPicker value={form.specialties} onChange={(v) => set("specialties", v)} options={specialties || []} testId="chef-specialties" />
          </div>
          <div className="rounded-2xl border border-border p-4 flex items-center justify-between gap-4" data-testid="chef-status-toggle">
            <div>
              <p className="font-bold text-ink inline-flex items-center gap-2">{form.status === "LIVE" ? <Eye size={16} className="text-leaf" /> : <EyeOff size={16} className="text-ink-soft" />} Profile is {form.status === "LIVE" ? "Live" : "Draft"}</p>
              <p className="text-xs text-ink-soft mt-0.5">{form.status === "LIVE" ? "Visible in the Chefs directory and search." : "Hidden from everyone except you."}</p>
            </div>
            <button type="button" role="switch" aria-checked={form.status === "LIVE"} data-testid="chef-status-switch" onClick={() => set("status", form.status === "LIVE" ? "DRAFT" : "LIVE")}
              className={`relative w-14 h-8 rounded-full transition-colors ${form.status === "LIVE" ? "bg-leaf" : "bg-slate-300"}`}>
              <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${form.status === "LIVE" ? "translate-x-7" : "translate-x-1"}`} />
            </button>
          </div>
          <button onClick={save} disabled={busy} data-testid="chef-profile-save"
            className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-coral hover:bg-coral-hover text-white font-bold transition-colors disabled:opacity-60">
            <Save size={17} /> {busy ? "Saving…" : isNew ? "Create Chef Profile" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
